import axios from 'axios'
import fileDownload from 'js-file-download'
import { IBulkModel } from './_models'
import { IDuplicateBarcodeModel } from '../../cors/_models'
import {API_URL} from '../../../../tenant/apiBase'

export const BULK_ACTION = `${API_URL}/api/partner-portal/product/bulk`
export const BULK_TEMPLATE = `${API_URL}/api/partner-portal/special/bulk-template`
export const BULK_IMAGES_UPLOAD = `${API_URL}/api/partner-portal/product/upload`
export const GET_DUPLICATE_BARCODES = `${API_URL}/api/partner-portal/product/duplicate-barcodes`
export const FIX_DUPLICATE_BARCODES = `${API_URL}/api/partner-portal/product/fix-duplicate-request`
// Dynamic Lumen endpoint (the same config-driven buildBulkTemplateDownload the
// manager "Add products bulk" page uses) — embeds live dropdowns for brand /
// category / recommended age / size / color / etc., so suppliers always get an
// up-to-date template instead of a hosted static xlsx. The path is versioned
// (…/v3) to bust Cloudflare + browser caches; bump it whenever the template
// shape changes (v3 = required columns grouped left, optional right). Must match
// the route in lumen/routes/partner_portal.php. Authenticated via the token header.
export const PRODUCTS_BULK_TEMPLATE = `${API_URL}/api/partner-portal/product/bulk/template/v3`
// Tenant-relative (API_URL = the tenant's own storefront): a hardcoded www.dumyah.com URL is both a
// cross-brand leak AND non-functional for other tenants (wrong origin/brand). Served from each
// storefront's own image dir.
export const PRODUCTS_UPDATE_BULK_TEMPLATE = `${API_URL}/image/data/partners/Update_by_bulk_empty.xlsx`
// A partner shop's account gets its own update sheet — Barcode | Shelf quantity | Price —
// built per store by the API (ProductController::downloadUpdateBulkTemplate); the
// endpoint picks the template from the signed-in account, so it needs the session token.
export const PRODUCTS_UPDATE_BULK_TEMPLATE_API = `${API_URL}/api/partner-portal/product/bulk/update-template`

export async function bulkAction(token: string | undefined, data: IBulkModel, language?: string, images?: any) {
  const formData = new FormData()

  formData.append('sheet', data.sheet)
  formData.append('action', data.action)
  if (typeof data.disable_active_offers !== 'undefined') {
    formData.append('disable_active_offers', data.disable_active_offers ? '1' : '0')
  }
  // if (images) {
  //   await uploadImagesWithBulk(images, token || '')
  // }

  return axios.post<any>(`${BULK_ACTION}`, formData, {
    responseType: 'blob',
    headers: { token: token || '', language: language || 'en', 'content-type': 'multipart/form-data' },
  })
}

// Async EDIT bulk: the backend (ProductController@bulk, action=edit) now queues
// the sheet and returns {job_id} as JSON instead of streaming the result blob.
// The supplier tracks progress + downloads the result on the Bulk Jobs page.
export async function enqueueBulkEdit(
  token: string | undefined,
  sheet: any,
  disable_active_offers: boolean,
  language?: string,
) {
  const formData = new FormData()
  formData.append('sheet', sheet)
  formData.append('action', 'edit')
  formData.append('disable_active_offers', disable_active_offers ? '1' : '0')
  return axios.post<{job_id?: number; status?: string; total_rows?: number; message?: string}>(
    `${BULK_ACTION}`,
    formData,
    {headers: {token: token || '', language: language || 'en', 'content-type': 'multipart/form-data'}},
  )
}

// Async ADD bulk: the backend (ProductController@bulk, action=add) now queues the
// sheet + the optional product image ZIP and returns {job_id} as JSON. The worker
// files the new-product requests, attaches the zip, auto-lists the
// already-in-catalog offers, and parks ambiguous variants for the Bulk Jobs
// review page — all the orchestration that used to run here on the client.
// The product image ZIP is uploaded separately via the resumable tus protocol
// (see tusUpload.ts) so a >100 MB ZIP can cross Cloudflare's 100 MB request-body
// cap in <100 MB chunks. Here we only pass the resulting upload key; the
// worker claims the reassembled ZIP server-side and attaches it in the
// background, exactly as it did with the old multipart `images` field.
export async function enqueueBulkAdd(
  token: string | undefined,
  sheet: any,
  images: BulkImagesZip | undefined,
  language?: string,
) {
  const formData = new FormData()
  formData.append('sheet', sheet)
  formData.append('action', 'add')
  if (images) {
    appendImagesZip(formData, images)
  }
  return axios.post<{job_id?: number; status?: string; total_rows?: number; message?: string}>(
    `${BULK_ACTION}`,
    formData,
    {headers: {token: token || '', language: language || 'en', 'content-type': 'multipart/form-data'}},
  )
}

/**
 * The images ZIP as a request carries it: the key of a finished chunked upload (tusUpload.ts), or
 * the file itself when the API cannot take it in pieces (see chunkedUploadUnavailable).
 */
export type BulkImagesZip = string | File

function appendImagesZip(formData: FormData, images: BulkImagesZip) {
  if (typeof images === 'string') {
    formData.append('images_upload_key', images)
  } else {
    formData.append('images', images)
  }
}

// Zip-only follow-up (no sheet): attach images to already-filed requests. A ZIP
// sent by key is queued by the API as a Bulk Job ({job_id}); a file sent the old
// way is attached while the request waits ({message}).
export async function uploadImagesWithBulk(images: BulkImagesZip, token: string | undefined) {
  const formData = new FormData()
  appendImagesZip(formData, images)
  return await axios.post<any>(`${BULK_IMAGES_UPLOAD}`, formData, {
    headers: { token: token || '' },
  })
}

// GET Bulk Template => download
export function getBulkTemplate(url: string, token: string | undefined) {
  return axios.get<any>(`${url}`, {
    responseType: 'blob',
    headers: { token: token || '' }
  })
}

/** Thrown by downloadBulkTemplate when the server sent something that is not a spreadsheet. */
export class TemplateDownloadError extends Error {}

/**
 * Every .xlsx is a ZIP container, and every ZIP starts with these four bytes.
 * Checked instead of the content-type header because the responses that actually
 * broke suppliers carried no useful type: the endpoint answered 200 with an empty
 * body (see below), and an edge error page arrives as text/html.
 */
const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04]

/** Read the first bytes of a blob. FileReader rather than Blob.arrayBuffer(), which
 *  older Safari and jsdom don't implement. */
function readHead(blob: Blob, length: number): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(blob.slice(0, length))
  })
}

async function looksLikeXlsx(blob: Blob): Promise<boolean> {
  if (blob.size < ZIP_MAGIC.length) {
    return false
  }
  const head = await readHead(blob, ZIP_MAGIC.length)
  return ZIP_MAGIC.every((byte, i) => head[i] === byte)
}

/**
 * Fetch a bulk template and save it, refusing to write a file that Excel cannot open.
 *
 * The callers used to hand the response blob straight to fileDownload() whatever it
 * contained. When the discounts endpoint regressed to an empty 200 (DEV-3787 — the Lumen→
 * Laravel migration turned FastExcel's download() into a response the controller had to
 * return, and it wasn't), that wrote a zero-byte file named .xlsx: the browser reported a
 * successful download, the portal showed no error, and the supplier only found out when
 * Excel refused the file with "the file format or file extension is not valid" — with
 * nothing on screen to connect it back to this button.
 *
 * A bad response is now a visible failure here rather than a corrupt file on disk. The
 * server-side defect is fixed, but this is the guard that keeps the next one legible.
 */
export async function downloadBulkTemplate(
  url: string,
  token: string | undefined,
  fileName: string,
): Promise<void> {
  const {data} = await getBulkTemplate(url, token)

  if (!(data instanceof Blob) || !(await looksLikeXlsx(data))) {
    throw new TemplateDownloadError(`template response from ${url} is not an xlsx`)
  }

  fileDownload(data, fileName)
}

// GET DUPLICATE BARCODES
export function getDuplicateBarcodes(token: string | undefined, language?: string) {
  return axios.post<{ requests: IDuplicateBarcodeModel[] }>(
    `${GET_DUPLICATE_BARCODES}`,
    {},
    { headers: { token: token || '', language: language || 'en' } }
  )
}

// FIX DUPLICATE BARCODES
export function fixDuplicateBarcode(keys: string[], token: string | undefined) {
  const formData = new FormData()

  keys.forEach((key) => {
    formData.append('request_products_id[]', key)
  })

  return axios.post<any>(`${FIX_DUPLICATE_BARCODES}`, formData, { headers: { token: token || '' } })
}
